// URL search-param name for the assignee filter, shared between the filter
// state (WorkQueue.tsx) and the assign mutation's invalidation fallback.
export const ASSIGNEE_URL_PARAM = 'assignee';

// Phase display labels - matches backend MACRO_LABELS, ordered for the GroupNav.
// Fallback while `meta.phases` is loading; the API value then takes precedence.
export const PHASES = [
    'Design',
    'Redesign',
    'Approval',
    'Manufacturing',
    'Treatment',
    'Refinement',
    'Retention',
    'Hold',
    'Done'
] as const;

export const IMPLEMENTED_PHASES: ReadonlySet<string> = new Set([
    'Design',
    'Approval',
    'Redesign',
    'Manufacturing',
    'Refinement',
    'Retention',
    'Hold',
    'Done'
]);

export const PENDING_MONITORING_START_STATUS_ID = 'Tx - 0. Pending Monitoring start';
export const TREATMENT_IN_PROGRESS_STATUS_ID = 'Tx - 1. Treatment in progress';
export const DM_VA_NEEDED_STATUS_ID = 'Tx - 2. DM VA needed';
export const CLINICAL_VA_NEEDED_STATUS_ID = 'Tx - 3. Clinical VA needed';

// Mirrors the backend's isInTreatmentStatus.
export const IN_TREATMENT_STATUSES: ReadonlySet<string> = new Set([
    PENDING_MONITORING_START_STATUS_ID,
    TREATMENT_IN_PROGRESS_STATUS_ID,
    DM_VA_NEEDED_STATUS_ID,
    CLINICAL_VA_NEEDED_STATUS_ID
]);

export const IMPLEMENTED_STATUSES: ReadonlySet<string> = new Set([
    TREATMENT_IN_PROGRESS_STATUS_ID,
    DM_VA_NEEDED_STATUS_ID,
    CLINICAL_VA_NEEDED_STATUS_ID,
    PENDING_MONITORING_START_STATUS_ID,
    'Init - 9.1. Waiting for post-approval redesign',
    'Init - 9.2. Review post-approval redesign'
]);

// Waiting-on-manufacturer steps show the MANUFACTURER STATUS column
// (patient_monday_data.manufacturer_case_status).
export const MANUFACTURER_STATUS_STATUSES: ReadonlySet<string> = new Set([
    'Init - 4. Waiting for design',
    'Init - 4.1. Design pre-approved',
    'Redesign - 2. Waiting for redesign',
    'Init - 9.1. Waiting for post-approval redesign',
    'Init - 12. Waiting for manufacturing',
    'Ref - 4. Waiting for Refinement design',
    'Ref - 4.1. Refinement design pre-approved',
    'Ref - 8. Waiting for manufacturing',
    'Ret - 4. Waiting for manufacturing'
]);

// statusMeta.task is null here (every edge is external, waiting on the practice), so each gets
// a frontend-only "View" task override in buildStatusLookup (util.ts).
export const FINAL_REVIEW_STATUS_ID = 'Init - 7. Review Dx';
export const FINAL_REVIEW_TASK_ID = 'final-review';

export const PENDING_APPROVAL_STATUS_ID = 'Init - 8. Pending approval';
export const APPROVAL_REVIEW_TASK_ID = 'approval-review';

export const SDI_REVIEW_STATUS_ID = 'Init - 13. Pending patient appointment update';
export const SDI_REVIEW_TASK_ID = 'sdi-review';

export const RDI_REVIEW_STATUS_ID = 'Ref - 9. Pending patient appointment update';
export const RDI_REVIEW_TASK_ID = 'rdi-review';

// Reached only via the Actions tab archive-case action, never an engine edge: never a
// work-queue button or a Force-status target.
export const ARCHIVED_STATUS_ID = 'Term - 3. Archived';

// Reached only via the "expire-stale-design" global action, never an edge; as a Force-status
// target it would skip the staleness rule and the dry-run.
export const DESIGN_EXPIRED_STATUS_ID = 'Term - 4. Patient design expired';

// Entered only by VA automation; never a Force-status target.
export const AUTOMATION_ONLY_STATUS_IDS: ReadonlySet<string> = new Set([
    DM_VA_NEEDED_STATUS_ID,
    CLINICAL_VA_NEEDED_STATUS_ID
]);

// Statuses exempt from overdue/stale aging
export const AGING_EXEMPT_STATUS_IDS: ReadonlySet<string> = new Set([
    TREATMENT_IN_PROGRESS_STATUS_ID,
    'Term - 1. Done',
    'Term - 2. Patient declined treatment',
    'Term - 4. Patient design expired',
    ARCHIVED_STATUS_ID
]);

// Treatment-phase VA steps: the phase runs for months by design, so only their step time is aged.
export const PHASE_AGING_EXEMPT_STATUS_IDS: ReadonlySet<string> = new Set([
    DM_VA_NEEDED_STATUS_ID,
    CLINICAL_VA_NEEDED_STATUS_ID
]);

// V1 cases need the 'refinement-design-approval' flag (services/practiceFeatureFlags.ts); V2 always
// reach it. Always visible in ForceStatusModal, blocked on submit when unreachable.
export const PENDING_REFINEMENT_APPROVAL_STATUS_ID = 'Ref - 6.1. Pending refinement approval';

export const ORTHO_REVIEW_STATUS_ID = 'Init - 7.1. Ortho review';
export const REDESIGN_ORTHO_REVIEW_STATUS_ID = 'Redesign - 5.1. Ortho review';

// V2 patients only; V1 cases go straight from Init-7 / Redesign-3 / Redesign-5 to Init-8
// and from Init-10 to Init-10.2.
export const V2_ONLY_STATUS_IDS: ReadonlySet<string> = new Set([
    ORTHO_REVIEW_STATUS_ID,
    REDESIGN_ORTHO_REVIEW_STATUS_ID,
    'Init - 10.1. Review SDI'
]);

// The backend swaps these in for their V1 action on V2 cases, so the UI never fires them.
export const V2_LANE_ACTIONS: ReadonlySet<string> = new Set([
    'approve-dx-review-v2',
    'approve-redesign-dx-review-v2',
    'approve-redesign-skip-dx-v2',
    'create-sdi-v2',
    'refinement-needed-odo-managed'
]);

// Their routes require review notes, which only the task bodies collect; generic surfaces post none.
export const NOTES_REQUIRED_ACTIONS: ReadonlySet<string> = new Set([
    'request-refinement-design-revision'
]);

// PHASE_HEX moved to src/utils/phaseColors.ts so it can be shared outside
// the work-queue feature folder without a second copy drifting from this one.

// Assignee slots every task-completion card shows; any other owner team (e.g. TC)
// gets its own cell only on the cases it owns.
export const CARD_ASSIGNEE_TEAMS = ['Clinical', 'Admin'] as const;
